import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/loading-spinner/LoadingSpinner';
import PropertyPictures from '../../components/property/property-pictures/PropertyPictures';
import PropertyDetails from '../../components/property/property-details/PropertyDetails';
import PropertyDescription from '../../components/property/property-description/PropertyDescription';
import Amenities from '../../components/amenities/Amenities';
import BERDetails from '../../components/BER-details/BERDetails';
import PropertyStatistics from '../../components/property/property-statistics/PropertyStatistics';
import LandlordInformation from '../../components/landlord-information/LandlordInformation';
import ContactLandlord from '../../components/contact-landlord/ContactLandlord';
import ShareModal from '../../components/property/property-card/ShareModal';
import { Listing } from '../../components/property/property-card/PropertyCardTypes';
import { useAuth } from '../../auth/firebaseAuth';
import {
  getDocumentByField,
  insertDocument,
  deleteDocument,
  queryDocuments,
  getDocumentById,
  AutoApplyUser,
  updateDocument,
} from '../../utility/firebaseHelpers';
import { Timestamp, where, serverTimestamp } from 'firebase/firestore';
import { HomeIcon, MagnifyingGlassIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import PropertyApplicationFAQ from '../../components/property-inquiry-faq/propertyInquiryFaq';
import MortgageCalculator from '../../components/property-tools/MortgageCalculator/MortgageCalculator';
import PropertyAlerts from '../../components/property-tools/PropertyAlerts/PropertyAlerts';
import { usePropertyViews } from '../../hooks/usePropertyViews';
import { ShareToApplyButton } from '../../components/share-to-apply/ShareToApplyButton';
import { SupportNetwork } from '../../components/support-network/SupportNetwork';
import FeedbackForm from '../../components/feedback-form/FeedbackForm';
import FeedbackCard from '../../components/feedback-card/FeedbackCard';
import { remoteConfig } from '../../utility/firebaseRemoteConfigHelper';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import { isElementInViewport } from '../../utility/viewportUtils';
import SimilarListings from '../../components/similar-listings/SimilarListings';
import PropertyVideo from '../../components/property-video/PropertyVideo';
import VirtualTour from '../../components/virtual-tour/VirtualTour';
import SocialProof from '../../components/social-proof/SocialProof';
import toast from 'react-hot-toast';
import AutoApplyCTA from '../../components/auto-apply-cta/AutoApplyCTA';
import PrintButton from '../../components/print-button/PrintButton';
import { formatAvailableDate } from '../../utility/date-formatter';
import PropertyCategoriesCarousel from '../../components/property-categories-carousel/PropertyCategoriesCarousel';
import ReportListingModal from '../../components/report-listing-modal/ReportListingModal';
import AccessibilityFeatures from '../../components/accessibility-features/AccessibilityFeatures';
import DiscoverAreasUpsell from '../../components/discover-areas/DiscoverAreasUpsell';
import {
  DraftListingView,
  InactiveListingView,
  PendingListingView,
} from '../../components/listing-status-views';

const PropertyViewPage: React.FC = () => {
  const { listingId } = useParams<{
    listingId: string;
  }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Listing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [currentShareUrl, setCurrentShareUrl] = useState('');
  const [currentShareTitle, setCurrentShareTitle] = useState('');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const analytics = useAnalyticsWithConsent();
  const viewStartTime = useRef(new Date());
  const interactionCount = useRef(0);
  const maxScrollDepth = useRef(0);
  const sectionViewTimes = useRef<Record<string, number>>({});
  const lastSectionChange = useRef(new Date());

  // Note: Initial PageView is tracked by the pixel in public/index.html
  // Only track specific property view events here

  usePropertyViews(listingId, () => {
    // Optimistically increment the local property view count
    // This ensures the UI shows the updated count immediately
    setProperty((prev) => {
      if (prev) {
        return { ...prev, propertyViews: prev.propertyViews + 1 };
      }
      return prev;
    });
  });

  useEffect(() => {
    if (property) {
      // Track initial property view
      analytics.trackPropertyView(property.id, {
        type: property.type,
        price: property.price,
        location: property.location,
        images: property.images,
        amenities: property.amenities,
        virtualTourUrl: property.virtualTourUrl,
      });

      // Track Facebook Pixel & Reddit Pixel ViewContent event
      analytics.trackPixelViewContent(
        {
          content_name: property.title,
          content_ids: [property.id.toString()],
          content_type: property.type,
          currency: 'EUR',
        },
        {
          email: currentUser?.email || undefined,
          externalId: currentUser?.uid || undefined,
        }
      );
    }
  }, [analytics, currentUser?.email, currentUser?.uid, property]);

  // Track scroll and section views
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const currentDepth = Math.round(((scrollTop + windowHeight) / documentHeight) * 100);

      maxScrollDepth.current = Math.max(maxScrollDepth.current, currentDepth);

      // Update section view times
      const sections = ['photos', 'details', 'description', 'amenities', 'contact'];
      sections.forEach((section) => {
        const element = document.getElementById(section);
        if (element && isElementInViewport(element)) {
          const now = new Date();
          sectionViewTimes.current[section] =
            (sectionViewTimes.current[section] || 0) +
            (now.getTime() - lastSectionChange.current.getTime());
          lastSectionChange.current = now;
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track interactions
  const trackInteraction = (type: string) => {
    interactionCount.current += 1;
    analytics.trackInteraction(type, {
      property_id: property?.id,
      scroll_depth: maxScrollDepth.current,
      time_since_view: new Date().getTime() - viewStartTime.current.getTime(),
      email: currentUser?.email || undefined,
      externalId: currentUser?.uid || undefined,
    });
  };

  useEffect(() => {
    setIsLoading(true);
    fetchPropertyDetails(listingId ?? '')
      .then((propertyData) => {
        const checkBookmarkStatus = async (id: string) => {
          if (currentUser) {
            const bookmarks = await queryDocuments('bookmarks', [
              where('userId', '==', currentUser.uid),
              where('listingId', '==', id),
            ]);
            setIsBookmarked(bookmarks.length > 0);
          } else {
            const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '[]');
            setIsBookmarked(bookmarks.includes(id));
          }
        };

        setProperty(propertyData);
        if (listingId) {
          checkBookmarkStatus(listingId);
        }
      })
      .catch((error) => console.error('Error fetching property details:', error))
      .finally(() => setIsLoading(false));
  }, [currentUser, listingId]);

  useEffect(() => {
    return () => {
      if (property) {
        // Track overall engagement
        analytics.trackPropertyEngagement(property.id, {
          viewDuration: new Date().getTime() - viewStartTime.current.getTime(),
          scrollDepth: maxScrollDepth.current,
          interactionCount: interactionCount.current,
          inquirySent: false, // Update based on actual inquiry status
          sectionViewTimes: sectionViewTimes.current,
        });

        // Track content effectiveness
        analytics.trackContentEffectiveness(property.id, {
          section_view_times: sectionViewTimes.current,
          most_viewed_section: Object.entries(sectionViewTimes.current).sort(
            ([, a], [, b]) => b - a
          )[0]?.[0],
          images_viewed: property.images.length,
          amenities_viewed: property.amenities.length,
        });

        // Track performance
        const navTiming = performance.getEntriesByType(
          'navigation'
        )[0] as PerformanceNavigationTiming;
        analytics.trackPerformance(`property_${property.id}`, {
          loadTime: navTiming.loadEventEnd,
          renderTime: navTiming.domComplete - navTiming.domInteractive,
          resourcesLoaded: performance.getEntriesByType('resource').length,
          timeToInteractive: navTiming.domInteractive,
        });
      }
    };
  }, [property]);

  const fetchPropertyDetails = async (id: string): Promise<Listing | null> => {
    const listing = await getDocumentByField<Listing>('listings', 'id', Number(id));
    return listing;
  };

  const handleBookmarkToggle = async () => {
    trackInteraction('bookmark');
    if (currentUser) {
      try {
        const bookmarks = await queryDocuments('bookmarks', [
          where('userId', '==', currentUser.uid),
          where('listingId', '==', listingId),
        ]);
        if (bookmarks.length === 0) {
          await insertDocument('bookmarks', {
            userId: currentUser.uid,
            listingId,
            createdAt: serverTimestamp(),
          });
          setIsBookmarked(true);
        } else {
          await deleteDocument('bookmarks', bookmarks[0].id);
          setIsBookmarked(false);
        }
      } catch (error) {
        console.error('Error toggling bookmark:', error);
      }
    } else {
      const bookmarks = JSON.parse(localStorage.getItem('bookmarks') || '[]');
      const updatedBookmarks = isBookmarked
        ? bookmarks.filter((bookmarkId: string) => bookmarkId !== listingId)
        : [...bookmarks, listingId];
      localStorage.setItem('bookmarks', JSON.stringify(updatedBookmarks));
      setIsBookmarked(!isBookmarked);
    }
  };

  const handleShare = async (listing: Listing) => {
    trackInteraction('share');
    const shareUrl = `${window.location.origin}/property/${listing.id}`;
    const shareTitle = `Check out this property: ${listing.title}`;
    setCurrentShareUrl(shareUrl);
    setCurrentShareTitle(shareTitle);
    setIsShareModalOpen(true);

    // Track share for Auto Apply feature if user is logged in and remote config: isAutoApplyEnabled is enabled
    if (currentUser && remoteConfig.isAutoApplyEnabled()) {
      try {
        // Get existing auto apply user document
        const autoApplyUser = await getDocumentById<AutoApplyUser>(
          'autoApplyUsers',
          currentUser.uid
        );

        if (autoApplyUser) {
          // Check if property already shared
          const alreadyShared = autoApplyUser.sharedProperties?.some(
            (share) => share.propertyId === listing.id.toString()
          );

          if (!alreadyShared) {
            const updatedShareCount = (autoApplyUser.shareCount || 0) + 1;
            if (updatedShareCount === 1) {
              toast.success('Share one more property to activate auto-apply feature!');
            }
            const updatedProperties = [
              ...(autoApplyUser.sharedProperties || []),
              {
                propertyId: listing.id.toString(),
                sharedAt: Timestamp.now(),
                sharedWith: 'social_share',
              },
            ];

            // Update document
            await updateDocument('autoApplyUsers', currentUser.uid, {
              shareCount: updatedShareCount,
              sharedProperties: updatedProperties,
              lastUpdated: Timestamp.now(),
            });

            // Check if user has reached share threshold
            if (updatedShareCount >= 2 && !autoApplyUser.isEnabled) {
              await updateDocument('autoApplyUsers', currentUser.uid, {
                isEnabled: true,
                activatedAt: Timestamp.now(),
                expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
              });

              toast.success(
                'Auto Apply feature activated! Go to auto-apply dashboard to set your preferences.'
              );
            }
          }
        } else {
          // Create new auto_apply_users document
          await insertDocument(
            'autoApplyUsers',
            {
              userId: currentUser.uid,
              isEnabled: false,
              shareCount: 1,
              sharedProperties: [
                {
                  propertyId: listing.id.toString(),
                  sharedAt: Timestamp.now(),
                  sharedWith: 'social_share',
                },
              ],
              createdAt: Timestamp.now(),
              lastUpdated: Timestamp.now(),
              stats: {
                totalApplications: 0,
                successfulApplications: 0,
              },
            },
            currentUser.uid
          );
        }
      } catch (error) {
        console.error('Error tracking share for Auto Apply:', error);
      }
    }
  };

  const handleReportListing = () => {
    trackInteraction('report_listing');
    setIsReportModalOpen(true);
  };

  if (isLoading) return <LoadingSpinner />;

  // Show status-specific messages for non-active listings
  if (property && property.status === 'draft') {
    return (
      <DraftListingView
        propertyId={property.id}
        propertyOwnerUid={property.propertyOwnerUid}
        currentUserId={currentUser?.uid}
      />
    );
  }

  if (property && property.status === 'inactive') {
    return <InactiveListingView />;
  }

  if (property && property.status === 'pending') {
    return (
      <PendingListingView
        propertyOwnerUid={property.propertyOwnerUid}
        currentUserId={currentUser?.uid}
      />
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 text-center">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <HomeIcon className="w-24 h-24 text-gray-300" />
                <div className="absolute -bottom-2 -right-2 bg-red-100 rounded-full p-2">
                  <ExclamationCircleIcon className="w-8 h-8 text-red-600" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Property Not Found
            </h1>

            {/* Description */}
            <p className="text-lg text-gray-600 mb-2">
              We couldn't find the property you're looking for.
            </p>
            <p className="text-gray-500 mb-8">
              It may have been removed, rented out, or the listing ID might be incorrect.
            </p>

            {/* Listing ID if available */}
            {listingId && (
              <div className="bg-gray-50 rounded-lg p-4 mb-8 border border-gray-200">
                <p className="text-sm text-gray-500 mb-1">Listing ID</p>
                <p className="font-mono text-gray-900 font-semibold">{listingId}</p>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/browse')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
                Browse All Properties
              </button>

              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-700 font-semibold rounded-lg border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all duration-300"
              >
                <HomeIcon className="w-5 h-5" />
                Go to Homepage
              </button>
            </div>

            {/* Additional Help */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Need help? Contact our support team at{' '}
                <a
                  href="mailto:support@rentsmart.ie"
                  className="text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  support@rentsmart.ie
                </a>
              </p>
            </div>
          </div>

          {/* Popular Searches Section */}
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
              Popular Searches
            </h3>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => navigate('/browse?location=Dublin')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition-colors duration-200 text-sm font-medium"
              >
                Dublin Properties
              </button>
              <button
                onClick={() => navigate('/browse?location=Cork')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition-colors duration-200 text-sm font-medium"
              >
                Cork Properties
              </button>
              <button
                onClick={() => navigate('/browse?location=Galway')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition-colors duration-200 text-sm font-medium"
              >
                Galway Properties
              </button>
              <button
                onClick={() => navigate('/browse?type=apartment')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition-colors duration-200 text-sm font-medium"
              >
                Apartments
              </button>
              <button
                onClick={() => navigate('/browse?type=house')}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition-colors duration-200 text-sm font-medium"
              >
                Houses
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-2 sm:py-2 md:py-2">
      <div className="container mx-auto px-2 sm:px-2 lg:px-8">
        {/* Property Pictures and Basic Info */}
        <div id="photos">
          <PropertyPictures
            images={property.images}
            title={property.title}
            isBookmarked={isBookmarked}
            handleBookmark={handleBookmarkToggle}
            handleShare={() => handleShare(property)}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8 lg:gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6 lg:space-y-8">
            <div id="details">
              <PropertyDetails
                location={property.location}
                type={property.type}
                listingIntent={property.listingIntent}
                price={property.price}
                bedrooms={property.bedrooms}
                bathrooms={property.bathrooms}
                size={typeof property.size === 'number' ? property.size : 0}
                availableFrom={formatAvailableDate(property.availableFrom)}
                dateAdded={formatAvailableDate(property.dateAdded)}
              />
            </div>
            <PrintButton property={property} />
            <div id="description">
              <PropertyDescription description={property.description} />
            </div>
            <div id="amenities">
              <Amenities amenities={property.amenities} />
            </div>
            {property.berRating && <BERDetails berRating={property.berRating} />}
            {property.accessibility && (
              <AccessibilityFeatures accessibility={property.accessibility} />
            )}
            <PropertyStatistics propertyViews={property.propertyViews} />
            {property.videoUrl && <PropertyVideo videoUrl={property.videoUrl} />}
            {property.virtualTourUrl && <VirtualTour tourUrl={property.virtualTourUrl} />}

            <ShareToApplyButton listing={property} />
            {/* <RandomInsight /> */}
          </div>

          {/* Sidebar Area */}
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div id="contact">
              {!property.isExternalListing && <LandlordInformation user={property.user} />}
              <ContactLandlord
                externalListingUrl={property.externalListingUrl}
                isExternalListing={property.isExternalListing}
                propertyTitle={property.title}
                listingId={listingId ?? ''}
                landlordId={property?.propertyOwnerUid ?? ''}
              />
            </div>
            <PropertyApplicationFAQ />
            {property.listingIntent === 'rent' ? (
              <PropertyAlerts variant="compact" />
            ) : (
              <MortgageCalculator />
            )}

            {/* Report Listing Section */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <button
                onClick={handleReportListing}
                className="w-full bg-gray-100 text-gray-600 px-6 py-3 rounded-lg hover:bg-gray-200 transition-all duration-300 font-medium"
              >
                Report this Listing
              </button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Report illegal or misleading content
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Sections with proper mobile spacing */}
      <div className="mt-8 sm:mt-12 space-y-8 sm:space-y-12">
        <PropertyCategoriesCarousel />

        {remoteConfig.isAutoApplyEnabled() && <AutoApplyCTA className="mb-0" />}

        <SimilarListings currentListing={property} />

        <SupportNetwork />

        <DiscoverAreasUpsell />

        <SocialProof userType="renter" />

        {remoteConfig.isFeedbackEnabled() && (
          <div>
            <FeedbackCard
              variant="compact"
              title="Quick Feedback"
              subtitle="Tell us what you think"
            />
            <FeedbackForm />
          </div>
        )}
      </div>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        url={currentShareUrl}
        title={currentShareTitle}
      />

      <ReportListingModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        listingId={listingId || ''}
        propertyTitle={property?.title || ''}
        currentUserId={currentUser?.uid}
      />
    </div>
  );
};

export default PropertyViewPage;
